import { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Pill, Search } from "lucide-react";
import {
  prescriptionTemplateQueryKeys,
  prescriptionTemplatesApi,
} from "../../../shared/api/prescriptionTemplates";
import { useDebounce } from "../../../shared/hooks/useDebounce";
import type {
  PrescriptionTemplateResponse,
  PrescriptionTemplateSummaryResponse,
} from "../../../shared/types/prescriptionTemplate";
import { apiError } from "../../../shared/api/client";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";

interface Props {
  open: boolean;
  onClose: () => void;
  onPick: (template: PrescriptionTemplateResponse) => void;
}

/**
 * Picks a prescription template — searches by code/name/ICD-10/diagnosis.
 * Confirming applies the template's full item list to the encounter's
 * prescription (caller responsibility — this modal only returns the choice).
 */
export function TemplatePickerModal({ open, onClose, onPick }: Props) {
  const [q, setQ] = useState("");
  const debounced = useDebounce(q, 200);

  useEffect(() => {
    if (!open) setQ("");
  }, [open]);

  const list = useQuery({
    queryKey: prescriptionTemplateQueryKeys.list(
      { q: debounced || null, visibleOnly: true },
      0
    ),
    queryFn: () =>
      prescriptionTemplatesApi.list(
        { q: debounced || null, visibleOnly: true },
        { page: 0, size: 30, sort: "name" }
      ),
    enabled: open,
  });

  return (
    <Dialog open={open} onOpenChange={(next) => !next && onClose()}>
      <DialogContent className="max-w-3xl sm:rounded-md">
        <DialogHeader>
          <DialogTitle>Chọn toa thuốc mẫu</DialogTitle>
          <DialogDescription>
            Chọn một toa mẫu để điền vào đơn thuốc của lần khám này. Bạn vẫn có
            thể chỉnh từng dòng sau khi áp dụng.
          </DialogDescription>
        </DialogHeader>

        <div className="relative">
          <Search
            className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground"
            aria-hidden="true"
          />
          <Input
            type="search"
            className="pl-9"
            placeholder="Tìm theo mã, tên, chẩn đoán, ICD-10…"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            autoFocus
          />
        </div>

        <div className="max-h-[55vh] space-y-2 overflow-y-auto">
          {list.isLoading && (
            <p className="text-sm text-muted-foreground">Đang tải…</p>
          )}
          {list.isError && (
            <div className="rounded-md border border-destructive/50 bg-destructive/10 px-3 py-2 text-sm text-destructive">
              {apiError(list.error).detail}
            </div>
          )}
          {!list.isLoading && (list.data?.content.length ?? 0) === 0 && (
            <div className="flex items-start gap-3 rounded-md border border-dashed bg-muted/40 px-4 py-6 text-sm">
              <Pill
                className="mt-0.5 h-5 w-5 shrink-0 text-muted-foreground"
                aria-hidden="true"
              />
              <div>
                <strong className="font-medium text-foreground">
                  Không có toa mẫu nào khớp.
                </strong>
                <p className="mt-1 text-muted-foreground">
                  Tạo toa mẫu mới tại{" "}
                  <a
                    href="/prescriptions/templates"
                    className="font-medium text-primary underline-offset-4 hover:underline"
                  >
                    Toa thuốc mẫu
                  </a>
                  .
                </p>
              </div>
            </div>
          )}
          {list.data?.content.map((t) => (
            <TemplateRow key={t.id} t={t} onPick={onPick} onClose={onClose} />
          ))}
        </div>
      </DialogContent>
    </Dialog>
  );
}

function TemplateRow({
  t,
  onPick,
  onClose,
}: {
  t: PrescriptionTemplateSummaryResponse;
  onPick: (full: PrescriptionTemplateResponse) => void;
  onClose: () => void;
}) {
  const [loading, setLoading] = useState(false);
  return (
    <div className="flex items-center gap-3 rounded-md border bg-card px-3 py-2">
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2 text-sm font-medium text-foreground">
          <span className="font-mono text-xs text-muted-foreground">
            {t.code}
          </span>
          <span className="truncate">{t.name}</span>
        </div>
        <div className="mt-0.5 flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-muted-foreground">
          {t.icd10Code && (
            <Badge variant="muted" className="font-mono">
              {t.icd10Code}
            </Badge>
          )}
          {t.diagnosis && <span>{t.diagnosis} ·</span>}
          <span>
            {t.durationDays} ngày · {t.itemCount} thuốc
          </span>
        </div>
      </div>
      <Button
        type="button"
        size="sm"
        disabled={loading}
        onClick={async () => {
          setLoading(true);
          try {
            const full = await prescriptionTemplatesApi.get(t.id);
            onPick(full);
            onClose();
          } finally {
            setLoading(false);
          }
        }}
      >
        {loading ? "Đang nạp…" : "Dùng mẫu này"}
      </Button>
    </div>
  );
}
